import config from "../../config/config.json";

const { CHAT_HTTP_BASE, CHAT_WS_BASE } = config;

console.log("CHAT_HTTP_BASE", CHAT_HTTP_BASE);
console.log("CHAT_WS_BASE", CHAT_WS_BASE);

/* ----------------------------- Helpers ----------------------------- */

export const getUserId = (u) => u?.id || u?._id || u?.userId || "";

export const convIdOf = (a, b) => {
    const u1 = String(a || "").trim();
    const u2 = String(b || "").trim();
    if (!u1 || !u2) return "";
    return u1 < u2 ? `room_${u1}_${u2}` : `room_${u2}_${u1}`;
};

export const groupConvId = (groupId) => {
    const gid = String(groupId || "").trim();
    return gid ? `g_${gid}` : "";
};

async function safeJson(res) {
    try {
        return await res.json();
    } catch {
        return {};
    }
}



export async function getMessages() {
    return { messages: [] };
}

export async function uploadFiles() {
    throw new Error("uploadFiles is disabled (no-mongo chat service). Use your Node backend upload instead.");
}

export async function downloadFile() {
    throw new Error("downloadFile is disabled (no-mongo chat service). Use your Node backend download instead.");
}

export async function checkedAssignedUser(gpId, userId) {
    return {
        gpId: String(gpId || "").trim(),
        userId: String(userId || "").trim(),
        assigned: true,
        convId: "",
        source: "disabled:no-mongo",
    };
}

/* ------------------------ Required HTTP API ------------------------ */

export async function setGroupMembers(groupId, memberIds, token) {
    const gid = String(groupId || "").trim();
    if (!gid) throw new Error("Missing groupId");
    if (!token) throw new Error("Missing token");

    const members = Array.isArray(memberIds)
        ? memberIds.map(String).map((s) => s.trim()).filter(Boolean)
        : [];

    const res = await fetch(`${CHAT_HTTP_BASE}/groups/members`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ groupId: gid, members }),
    });

    const data = await safeJson(res);
    if (!res.ok) {
        throw new Error(data?.error || data?.message || `Failed to set group members (HTTP ${res.status})`);
    }
    return data;
}

/* ----------------------------- ChatSocket ----------------------------- */

export class ChatSocket {
    constructor({
        token,
        userId,
        name,
        onOpen,
        onClose,
        onError,
        onEvent,
        autoReconnect = true,
        reconnectDelayMs = 1200,
        maxReconnectDelayMs = 12000,
    }) {
        this.token = token;
        this.userId = String(userId || "").trim();
        this.name = name;

        this.onOpen = onOpen;
        this.onClose = onClose;
        this.onError = onError;
        this.onEvent = onEvent;

        this.autoReconnect = autoReconnect;
        this.reconnectDelayMs = reconnectDelayMs;
        this.maxReconnectDelayMs = maxReconnectDelayMs;

        this.ws = null;
        this.closedByUser = false;
        this._reconnectTimer = null;
        this._attempt = 0;
    }

    connect() {
        if (!this.token) throw new Error("token required for WS");
        if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) return;

        this.closedByUser = false;

        const ws = new WebSocket(CHAT_WS_BASE);
        this.ws = ws;

        ws.onopen = () => {
            this._attempt = 0;
            try {
                ws.send(
                    JSON.stringify({
                        type: "auth",
                        data: {
                            token: this.token,
                            userId: this.userId || "",
                            name: this.name || "",
                        },
                    })
                );
            } catch {
                // ignore
            }
            this.onOpen?.();
        };

        ws.onmessage = (evt) => {
            try {
                const env = JSON.parse(evt.data);
                if (env?.type && this.onEvent) this.onEvent(env.type, env.data);
            } catch {
                // ignore
            }
        };

        ws.onerror = (e) => this.onError?.(e);

        ws.onclose = () => {
            this.onClose?.();

            if (this.autoReconnect && !this.closedByUser) {
                clearTimeout(this._reconnectTimer);

                this._attempt += 1;
                const delay = Math.min(
                    this.reconnectDelayMs * Math.pow(1.6, this._attempt),
                    this.maxReconnectDelayMs
                );
                this._reconnectTimer = setTimeout(() => this.connect(), delay);
            }
        };
    }

    disconnect() {
        this.closedByUser = true;
        clearTimeout(this._reconnectTimer);
        try {
            this.ws?.close();
        } catch {
            // ignore
        }
        this.ws = null;
    }

    isReady() {
        return this.ws && this.ws.readyState === 1;
    }

    send(type, data) {
        if (!this.isReady()) return false;
        try {
            this.ws.send(JSON.stringify({ type, data }));
            return true;
        } catch {
            return false;
        }
    }

    /* ----------------------------- DM ----------------------------- */

    sendMessage({ convId, to, text, files, data }) {
        return this.send("message:send", {
            convId,
            to,
            text: text || "",
            files: Array.isArray(files) ? files : [],
            data: data ?? null,
        });
    }

    sendMessageNewAgain({ chatInfo, additionalInfo }) {
        return this.send("messageNewAgain:send", {
            chatInfo: chatInfo ?? {},
            additionalInfo: additionalInfo ?? {},
        });
    }

    typingStart(convId, toUserId) {
        return this.send("typing:start", { convId, toUserId });
    }

    typingStop(convId, toUserId) {
        return this.send("typing:stop", { convId, toUserId });
    }

    markSeen(convId, toUserId, messageId) {
        return this.send("message:seen", { convId, toUserId, messageId });
    }

    /* ----------------------------- GROUP ----------------------------- */

    joinGroup({ convId, groupId, members }) {
        return this.send("group:join", {
            convId,
            groupId,
            members: Array.isArray(members) ? members : [],
        });
    }



    sendGroupMessage({ convId, groupId, text, files, data }) {
        return this.send("group:send", {
            convId,
            groupId,
            text: text || "",
            files: Array.isArray(files) ? files : [],
            data: data ?? null,
        });
    }

    groupTypingStart(convId, groupId) {
        return this.send("group:typing:start", { convId, groupId });
    }

    groupTypingStop(convId, groupId) {
        return this.send("group:typing:stop", { convId, groupId });
    }

    markGroupSeen(convId, messageId) {
        return this.send("group:seen", { convId, messageId });
    }

    webrtcSignal(payload) {
        return this.send("webrtc:signal", payload);
    }
}